
import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, collection, getDocs, addDoc, updateDoc, doc, deleteDoc, getDoc, setDoc, query, where, writeBatch, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { getAuth, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, updatePassword, User as FirebaseUser } from 'firebase/auth';
import { CalendarEvent, LabEntry, User, AppConfig, RotationWeek, EducationEvent, Inpatient, OnCallCaseLog, SurgicalLog, ReportExample } from '../types';
import { MOCK_EVENTS, MOCK_LABS, USERS, ROTATION_SCHEDULE, EDUCATION_EVENTS, MOCK_INPATIENTS, MOCK_ON_CALL_LOGS } from '../constants';

const firebaseConfig = {
  apiKey: "AIzaSyCpmEsGwJBiiVStWOIChyVSLKzMNW7ARR8",
  authDomain: "gen-lang-client-0767315944.firebaseapp.com",
  projectId: "gen-lang-client-0767315944",
  storageBucket: "gen-lang-client-0767315944.firebasestorage.app",
  messagingSenderId: "510408680136",
  appId: "1:510408680136:web:2b10ca4a79af819c1c3bfd",
  measurementId: "G-VJ3WFTRH27"
};

const hasKeys = firebaseConfig.apiKey !== "YOUR_API_KEY_HERE";

let db: any = null;
let auth: any = null;

if (hasKeys) {
  try {
    const app = getApps().length === 0 
      ? initializeApp(firebaseConfig) 
      : getApp();
      
    db = getFirestore(app);
    auth = getAuth(app);
  } catch (e) {
    console.warn('Firebase config found but initialization failed.', e);
  }
}

const getLocal = <T>(key: string, defaultVal: T): T => {
    const s = localStorage.getItem(key);
    return s ? JSON.parse(s) : defaultVal;
};
const setLocal = (key: string, val: any) => localStorage.setItem(key, JSON.stringify(val));

const sanitize = <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
};

export const authService = {
    login: (email: string, pass: string) => {
        if (!auth) throw new Error("Auth not configured");
        return signInWithEmailAndPassword(auth, email, pass);
    },
    signup: (email: string, pass: string) => {
        if (!auth) throw new Error("Auth not configured");
        return createUserWithEmailAndPassword(auth, email, pass);
    },
    logout: () => {
        if (!auth) return Promise.resolve();
        return signOut(auth);
    },
    updatePassword: (user: FirebaseUser, newPass: string) => {
        return updatePassword(user, newPass);
    },
    onAuthStateChanged: (callback: (user: FirebaseUser | null) => void) => {
        if (!auth) return () => {};
        return onAuthStateChanged(auth, callback);
    }
};

export const dbService = {
  isConnected: () => hasKeys && !!db,

  // --- REAL-TIME SUBSCRIPTIONS ---
  subscribeToEvents(callback: (events: CalendarEvent[]) => void) {
      if (db) {
          const q = query(collection(db, 'events'));
          return onSnapshot(q, (snapshot) => {
              const dbEvents = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as CalendarEvent));
              const eventMap = new Map<string, CalendarEvent>();
              MOCK_EVENTS.forEach(evt => eventMap.set(`${evt.date}_${evt.category}`, evt));
              dbEvents.forEach(evt => eventMap.set(`${evt.date}_${evt.category}`, evt));
              callback(Array.from(eventMap.values()));
          });
      } else {
          const localEvents = getLocal('mission_op_events', MOCK_EVENTS);
          const eventMap = new Map<string, CalendarEvent>();
          MOCK_EVENTS.forEach(evt => eventMap.set(`${evt.date}_${evt.category}`, evt));
          localEvents.forEach(evt => eventMap.set(`${evt.date}_${evt.category}`, evt));
          callback(Array.from(eventMap.values()));
          return () => {};
      }
  },

  subscribeToRotations(callback: (rotations: RotationWeek[]) => void) {
      if (db) {
          const q = query(collection(db, 'rotations'));
          return onSnapshot(q, (snapshot) => {
              if (snapshot.empty) {
                  for(const week of ROTATION_SCHEDULE) {
                      addDoc(collection(db, 'rotations'), sanitize(week));
                  }
                  callback(ROTATION_SCHEDULE);
              } else {
                  const data = snapshot.docs.map(d => d.data() as RotationWeek).sort((a,b) => a.weekStart.localeCompare(b.weekStart));
                  callback(data);
              }
          });
      } else {
          callback(getLocal('mission_op_rotations', ROTATION_SCHEDULE));
          return () => {};
      }
  },

  subscribeToLabs(callback: (labs: LabEntry[]) => void) {
      if (db) {
          const q = query(collection(db, 'labs'));
          return onSnapshot(q, (snapshot) => {
              const labs = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as LabEntry));
              callback(labs);
          });
      } else {
          callback(getLocal('mission_op_labs', MOCK_LABS));
          return () => {};
      }
  },

  subscribeToInpatients(callback: (patients: Inpatient[]) => void) {
      if (db) {
          const q = query(collection(db, 'inpatients'));
          return onSnapshot(q, (snapshot) => {
              const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Inpatient));
              callback(data);
          });
      } else {
          callback(getLocal('mission_op_inpatients', MOCK_INPATIENTS));
          return () => {};
      }
  },

  subscribeToEducationEvents(callback: (events: EducationEvent[]) => void) {
      if (db) {
          const q = query(collection(db, 'education_events'));
          return onSnapshot(q, async (snapshot) => {
              if(snapshot.empty) {
                   for (const evt of EDUCATION_EVENTS) {
                        const safeEvt = { ...evt, articles: evt.articles.map(a => ({ ...a, url: undefined })) };
                        await setDoc(doc(db, 'education_events', safeEvt.id), sanitize(safeEvt));
                   }
                   callback(EDUCATION_EVENTS);
              } else {
                  const data = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as EducationEvent));
                  const sorted = data.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
                  callback(sorted);
              }
          });
      } else {
          callback(getLocal('mission_op_education', EDUCATION_EVENTS));
          return () => {};
      }
  },

  subscribeToOnCallLogs(callback: (logs: OnCallCaseLog[]) => void) {
      if (db) {
          const q = query(collection(db, 'on_call_logs'));
          return onSnapshot(q, (snapshot) => {
              const logs = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as OnCallCaseLog));
              callback(logs);
          });
      } else {
          callback(getLocal('mission_op_on_call_logs', MOCK_ON_CALL_LOGS));
          return () => {};
      }
  },

  subscribeToSurgicalLogs(callback: (logs: SurgicalLog[]) => void) {
      if (db) {
          const q = query(collection(db, 'surgical_logs'));
          return onSnapshot(q, (snapshot) => {
              const logs = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SurgicalLog));
              callback(logs);
          });
      } else {
          callback(getLocal('mission_op_surgical_logs', []));
          return () => {};
      }
  },

  async saveReportExample(example: ReportExample): Promise<void> {
      const localExamples = getLocal<ReportExample[]>('mission_op_report_examples', []);
      localExamples.push(example);
      if (localExamples.length > 20) localExamples.shift();
      setLocal('mission_op_report_examples', localExamples);

      if (db) {
          try {
              await addDoc(collection(db, 'report_examples'), sanitize(example));
          } catch(e) { console.error('Error saving learning example:', e); }
      }
  },

  async getRecentExamples(procedureName?: string): Promise<ReportExample[]> {
      if (db) {
          try {
              let q = query(collection(db, 'report_examples'), orderBy('timestamp', 'desc'), limit(3));
              const snapshot = await getDocs(q);
              const examples = snapshot.docs.map(d => d.data() as ReportExample);
              
              if (procedureName) {
                  const relevant = examples.filter(e => 
                      e.procedure.toLowerCase().includes(procedureName.toLowerCase()) ||
                      procedureName.toLowerCase().includes(e.procedure.toLowerCase())
                  );
                  if (relevant.length > 0) return relevant;
              }
              return examples;
          } catch(e) { console.error('Error fetching learning examples:', e); }
      }
      return getLocal('mission_op_report_examples', []);
  },

  async getAppConfig(): Promise<AppConfig> {
    const DEFAULT_CONFIG: AppConfig = {
        masterSheetUrl: "https://nvashare-my.sharepoint.com/:x:/r/personal/connor_pyne_nva_com/Documents/2025%20On-Call%20Surgery%20Team%20Log%20%26%20Schedule.xlsx?d=w4c7330b3f007480a802946364ace72a3&csf=1&web=1&e=ea9OsD"
    };

    if (db) {
        try {
            const docRef = doc(db, 'config', 'global_config');
            const snap = await getDoc(docRef);
            if (snap.exists()) {
                return snap.data() as AppConfig;
            }
            await setDoc(docRef, DEFAULT_CONFIG);
            return DEFAULT_CONFIG;
        } catch(e) { console.error('Config fetch error, using local/default:', e); }
    }
    return getLocal('mission_op_config', DEFAULT_CONFIG);
  },

  async saveAppConfig(config: AppConfig): Promise<void> {
     setLocal('mission_op_config', config);
     if (db) {
         try {
             await setDoc(doc(db, 'config', 'global_config'), sanitize(config));
         } catch (e) { console.error('Config save error:', e); }
     }
  },

  async getEvents(): Promise<CalendarEvent[]> {
    if (db) {
      const snapshot = await getDocs(collection(db, 'events'));
      return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as CalendarEvent));
    }
    return getLocal('mission_op_events', MOCK_EVENTS);
  },

  async saveEvent(event: CalendarEvent): Promise<void> {
    const events = getLocal<CalendarEvent[]>('mission_op_events', MOCK_EVENTS); 
    const existingIdx = events.findIndex(e => e.date === event.date && e.category === event.category);
    const newEvents = existingIdx >= 0 ? events.map((e, i) => i === existingIdx ? event : e) : [...events, event];
    setLocal('mission_op_events', newEvents);

    if (db) {
      try {
          const q = query(collection(db, 'events'), where('date', '==', event.date), where('category', '==', event.category));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
              const docId = querySnapshot.docs[0].id;
              await updateDoc(doc(db, 'events', docId), sanitize(event) as any);
          } else {
              await addDoc(collection(db, 'events'), sanitize(event));
          }
      } catch(e) { console.error('Event save error:', e); }
    }
  },

  async deleteEvent(date: string, category: string): Promise<void> {
      const events = getLocal<CalendarEvent[]>('mission_op_events', MOCK_EVENTS);
      const newEvents = events.filter(e => !(e.date === date && e.category === category));
      setLocal('mission_op_events', newEvents);

      if(db) {
          try {
             const q = query(collection(db, 'events'), where('date', '==', date), where('category', '==', category));
             const querySnapshot = await getDocs(q);
             querySnapshot.forEach(async (d) => {
                 await deleteDoc(doc(db, 'events', d.id));
             });
          } catch(e) { console.error('Event delete error:', e); }
      }
  },

  async getRotations(): Promise<RotationWeek[]> {
    if(db) {
        const snapshot = await getDocs(collection(db, 'rotations'));
        if(!snapshot.empty) {
            return snapshot.docs.map(d => d.data() as RotationWeek).sort((a,b) => a.weekStart.localeCompare(b.weekStart));
        }
    }
    return getLocal('mission_op_rotations', ROTATION_SCHEDULE);
  },

  async saveRotation(rotation: RotationWeek): Promise<void> {
    const rotations = getLocal<RotationWeek[]>('mission_op_rotations', ROTATION_SCHEDULE);
    const idx = rotations.findIndex(r => r.weekStart === rotation.weekStart);
    const newRotations = idx >= 0 ? rotations.map((r, i) => i === idx ? rotation : r) : [...rotations, rotation];
    setLocal('mission_op_rotations', newRotations);

    if(db) {
        try {
            const q = query(collection(db, 'rotations'), where('weekStart', '==', rotation.weekStart));
            const querySnapshot = await getDocs(q);
            if(!querySnapshot.empty) {
                const docId = querySnapshot.docs[0].id;
                await updateDoc(doc(db, 'rotations', docId), sanitize(rotation) as any);
            } else {
                await addDoc(collection(db, 'rotations'), sanitize(rotation));
            }
        } catch(e) { console.error('Rotation save error:', e); }
    }
  },

  async seedDefaultSchedule(): Promise<RotationWeek[]> {
      setLocal('mission_op_rotations', ROTATION_SCHEDULE);
      if (!db) return ROTATION_SCHEDULE;
      try {
          const snapshot = await getDocs(collection(db, 'rotations'));
          const batch = writeBatch(db);
          snapshot.docs.forEach(d => batch.delete(d.ref));
          await batch.commit();
          for(const week of ROTATION_SCHEDULE) {
             await addDoc(collection(db, 'rotations'), sanitize(week));
          }
      } catch(e) { console.error("Error reseeding schedule:", e); }
      return ROTATION_SCHEDULE;
  },

  async getLabs(): Promise<LabEntry[]> {
    if (db) {
        const snapshot = await getDocs(collection(db, 'labs'));
        return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as LabEntry));
    }
    return getLocal('mission_op_labs', MOCK_LABS);
  },

  async saveLab(lab: LabEntry): Promise<void> {
    const labs = getLocal<LabEntry[]>('mission_op_labs', MOCK_LABS);
    const exists = labs.find(l => l.id === lab.id);
    const newLabs = exists ? labs.map(l => l.id === lab.id ? lab : l) : [lab, ...labs];
    setLocal('mission_op_labs', newLabs);

    if (db) {
       try {
         await setDoc(doc(db, 'labs', lab.id), sanitize(lab));
       } catch(e) { console.error('Lab save error:', e); }
    }
  },

  async deleteLab(id: string): Promise<void> {
      const labs = getLocal<LabEntry[]>('mission_op_labs', MOCK_LABS);
      const newLabs = labs.filter(l => l.id !== id);
      setLocal('mission_op_labs', newLabs);

      if (db) {
          try {
              await deleteDoc(doc(db, 'labs', id));
          } catch(e) { console.error('Lab delete error:', e); }
      }
  },

  async getInpatients(): Promise<Inpatient[]> {
    if (db) {
      const snapshot = await getDocs(collection(db, 'inpatients'));
      return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as Inpatient));
    }
    return getLocal('mission_op_inpatients', MOCK_INPATIENTS);
  },

  async saveInpatient(patient: Inpatient): Promise<void> {
    const patients = getLocal<Inpatient[]>('mission_op_inpatients', MOCK_INPATIENTS);
    const exists = patients.find(p => p.id === patient.id);
    const newPatients = exists ? patients.map(p => p.id === patient.id ? patient : p) : [patient, ...patients];
    setLocal('mission_op_inpatients', newPatients);

    if (db) {
       try {
         await setDoc(doc(db, 'inpatients', patient.id), sanitize(patient));
       } catch(e) { console.error('Inpatient save error:', e); }
    }
  },

  async deleteInpatient(id: string): Promise<void> {
      const patients = getLocal<Inpatient[]>('mission_op_inpatients', MOCK_INPATIENTS);
      const newPatients = patients.filter(p => p.id !== id);
      setLocal('mission_op_inpatients', newPatients);

      if (db) {
          try {
              await deleteDoc(doc(db, 'inpatients', id));
          } catch(e) { console.error('Inpatient delete error:', e); }
      }
  },

  async getOnCallLogs(): Promise<OnCallCaseLog[]> {
    if (db) {
        const snapshot = await getDocs(collection(db, 'on_call_logs'));
        return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as OnCallCaseLog));
    }
    return getLocal('mission_op_on_call_logs', MOCK_ON_CALL_LOGS);
  },

  async saveOnCallLog(log: OnCallCaseLog): Promise<void> {
    const logs = getLocal<OnCallCaseLog[]>('mission_op_on_call_logs', MOCK_ON_CALL_LOGS);
    const exists = logs.find(l => l.id === log.id);
    const newLogs = exists ? logs.map(l => l.id === log.id ? log : l) : [log, ...logs];
    setLocal('mission_op_on_call_logs', newLogs);

    if (db) {
       try {
         await setDoc(doc(db, 'on_call_logs', log.id), sanitize(log));
       } catch(e) { console.error('Log save error:', e); }
    }
  },

  async deleteOnCallLog(id: string): Promise<void> {
      const logs = getLocal<OnCallCaseLog[]>('mission_op_on_call_logs', MOCK_ON_CALL_LOGS);
      const newLogs = logs.filter(l => l.id !== id);
      setLocal('mission_op_on_call_logs', newLogs);

      if (db) {
          try {
              await deleteDoc(doc(db, 'on_call_logs', id));
          } catch(e) { console.error('Log delete error:', e); }
      }
  },

  async getSurgicalLogs(): Promise<SurgicalLog[]> {
    if (db) {
        const snapshot = await getDocs(collection(db, 'surgical_logs'));
        return snapshot.docs.map(d => ({ ...d.data(), id: d.id } as SurgicalLog));
    }
    return getLocal('mission_op_surgical_logs', []);
  },

  async saveSurgicalLog(log: SurgicalLog): Promise<void> {
    const logs = getLocal<SurgicalLog[]>('mission_op_surgical_logs', []);
    const exists = logs.find(l => l.id === log.id);
    const newLogs = exists ? logs.map(l => l.id === log.id ? log : l) : [log, ...logs];
    setLocal('mission_op_surgical_logs', newLogs);

    if (db) {
       try {
         await setDoc(doc(db, 'surgical_logs', log.id), sanitize(log));
       } catch(e) { console.error('Surgical log save error:', e); }
    }
  },

  async deleteSurgicalLog(id: string): Promise<void> {
      const logs = getLocal<SurgicalLog[]>('mission_op_surgical_logs', []);
      const newLogs = logs.filter(l => l.id !== id);
      setLocal('mission_op_surgical_logs', newLogs);

      if (db) {
          try {
              await deleteDoc(doc(db, 'surgical_logs', id));
          } catch(e) { console.error('Surgical log delete error:', e); }
      }
  },

  async getEducationEvents(): Promise<EducationEvent[]> {
    if (db) {
      const snapshot = await getDocs(collection(db, 'education_events'));
      const events = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as EducationEvent));
      return events.sort((a,b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }
    return getLocal('mission_op_education', EDUCATION_EVENTS);
  },

  async saveEducationEvent(event: EducationEvent): Promise<void> {
      const events = getLocal<EducationEvent[]>('mission_op_education', EDUCATION_EVENTS);
      const idx = events.findIndex(e => e.id === event.id);
      const newEvents = idx >= 0 ? events.map((e, i) => i === idx ? event : e) : [...events, event];
      setLocal('mission_op_education', newEvents);

      if (db) {
          try {
              const eventToSave = { ...event };
              if (eventToSave.articles) {
                  eventToSave.articles = eventToSave.articles.map(a => {
                      const articleCopy = { ...a };
                      delete (articleCopy as any).url; 
                      return articleCopy;
                  });
              }
              const sanitizedEvent = sanitize(eventToSave);
              const docRef = doc(db, 'education_events', event.id);
              await setDoc(docRef, sanitizedEvent);
          } catch(e) { console.error('Education save error:', e); }
      }
  },

  async deleteEducationEvent(id: string): Promise<void> {
      const events = getLocal<EducationEvent[]>('mission_op_education', EDUCATION_EVENTS);
      const newEvents = events.filter(e => e.id !== id);
      setLocal('mission_op_education', newEvents);

      if (db) {
          try {
              await deleteDoc(doc(db, 'education_events', id));
          } catch(e) { console.error('Education delete error:', e); }
      }
  },

  async uploadArticleFile(articleId: string, file: File): Promise<void> {
    if (!db) return; 
    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
    const base64 = await base64Promise;
    const CHUNK_SIZE = 500 * 1024;
    const totalChunks = Math.ceil(base64.length / CHUNK_SIZE);
    try {
        for (let i = 0; i < totalChunks; i += 10) {
            const batch = writeBatch(db);
            const limit = Math.min(i + 10, totalChunks);
            for (let j = i; j < limit; j++) {
                const chunk = base64.slice(j * CHUNK_SIZE, (j + 1) * CHUNK_SIZE);
                batch.set(doc(db, 'article_chunks', `${articleId}_${j}`), { articleId, index: j, data: chunk });
            }
            await batch.commit();
        }
    } catch(e) { throw e; }
  },

  async downloadArticleFile(articleId: string): Promise<string | null> {
      if (!db) return null;
      try {
          const q = query(collection(db, 'article_chunks'), where('articleId', '==', articleId));
          const snapshot = await getDocs(q);
          if (snapshot.empty) return null;
          const chunks = snapshot.docs.map(d => d.data() as { index: number, data: string });
          chunks.sort((a, b) => a.index - b.index);
          return chunks.map(c => c.data).join('');
      } catch(e) { return null; }
  },

  async deleteArticleFile(articleId: string): Promise<void> {
      if (!db) return;
      try {
          const q = query(collection(db, 'article_chunks'), where('articleId', '==', articleId));
          const snapshot = await getDocs(q);
          const batch = writeBatch(db);
          snapshot.docs.forEach((doc) => { batch.delete(doc.ref); });
          await batch.commit();
      } catch (e) { }
  },

  async getUserProfile(uid: string): Promise<User | null> {
      if (!db) return null;
      try {
          const docRef = doc(db, 'users', uid);
          const snap = await getDoc(docRef);
          if (snap.exists()) {
              const data = snap.data() as User;
              if (data.email === 'kevinsgrisham@gmail.com') { data.isAdmin = true; }
              return data;
          }
      } catch (e) { }
      return null;
  },

  async createUserProfile(uid: string, data: Partial<User>): Promise<void> {
      const ALL_VIEWS = ['dashboard', 'inpatients', 'reports', 'caselogs', 'calendar', 'labs', 'education', 'drugs', 'settings'];
      let existingProfile: User | null = null;
      const users = await this.getUsers();
      if (data.firstName && data.lastName) {
          const targetName = `${data.firstName.trim().toLowerCase()} ${data.lastName.trim().toLowerCase()}`;
          existingProfile = users.find(u => {
              const uName = `${u.firstName?.trim().toLowerCase() || ''} ${u.lastName.trim().toLowerCase()}`;
              return uName.trim() === targetName && u.id !== uid;
          }) || null;
      }
      const newUser: User = {
          id: uid,
          lastName: data.lastName || 'User',
          firstName: data.firstName || '',
          email: data.email || '',
          role: (existingProfile?.role || data.role || 'Resident') as any,
          phone: '',
          allowedViews: existingProfile?.allowedViews || ALL_VIEWS,
          isAdmin: existingProfile?.isAdmin || (data.email === 'kevinsgrisham@gmail.com')
      };
      if (db) {
          await setDoc(doc(db, 'users', uid), newUser);
          if (existingProfile) { await deleteDoc(doc(db, 'users', existingProfile.id)); }
      }
  },

  async getUsers(): Promise<User[]> {
      if (db) {
          try {
              const snapshot = await getDocs(collection(db, 'users'));
              if (!snapshot.empty) {
                 const dbUsers = snapshot.docs.map(d => ({ ...d.data(), id: d.id } as User));
                 return dbUsers.map(u => ({ ...u, isAdmin: u.email === 'kevinsgrisham@gmail.com' ? true : u.isAdmin }));
              }
              return [];
          } catch (e) { }
      }
      return getLocal('mission_op_users', USERS);
  },

  async seedDefaultUsers(): Promise<User[]> {
      if (!db) return USERS;
      const batch = writeBatch(db);
      for (const user of USERS) {
           const { password, ...safeUser } = user as any;
           batch.set(doc(db, 'users', user.id), sanitize(safeUser));
      }
      await batch.commit();
      return USERS;
  },

  async updateUser(user: User): Promise<void> {
    if (db) {
      try { await updateDoc(doc(db, 'users', user.id), sanitize(user) as any); } catch (e) { }
    }
  },

  async deleteUser(uid: string): Promise<void> {
      if (db) {
          try { await deleteDoc(doc(db, 'users', String(uid))); } catch (e) { throw e; }
      }
  },

  async adminCreateUser(userData: User): Promise<void> {
      if (db) { await setDoc(doc(db, 'users', userData.id), userData); }
  }
};
